import { EColorVariablesV2 } from '@/types/theme-packs.types';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import ProjectColorIcon from '@ui/icons/icon/project-color-icon';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import { useMemo, useState } from 'react';
import SessionMetadataModal from './session-metadata-modal';
import { getWorkingProjectSessions } from '@/store/slices/project/selectors';
import { useAppSelector } from '@/store/hooks';
import styles from '../../widget.module.css';
import Icon from '@ui/icons/icon';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { ESettingsAdvancedWidgetLabels } from '../../settings.types';

const SessionMetadataWidget = () => {
  const sessions = useAppSelector(getWorkingProjectSessions);
  const [open, setOpen] = useState(false);

  const { uniqueDescriptors, sessionMap } = useMemo(() => {
    const filteredSessions = sessions?.filter((i) => i.internal_descriptor !== null) ?? [];

    const descriptors = new Set<string>();
    const sessionList = [];

    for (const item of filteredSessions) {
      descriptors.add(item.internal_descriptor || '');
      sessionList.push(item.session);
    }

    return {
      uniqueDescriptors: [...descriptors],
      sessionMap: sessionList,
    };
  }, [sessions]);

  const isEditing = uniqueDescriptors.length;

  const toggleModal = () => setOpen((prevState) => !prevState);

  const renderButton = () => {
    if (isEditing) {
      return (
        <Button
          backgroundColor={getThemeColorCSSVariable()}
          padding="16px 24px"
          height="32px"
          onClick={toggleModal}
          disabledOpacity={0.6}
          hoverOpacity={0.8}
          className={`${styles['edit-button']} flex ai-center gap-6`}
        >
          <Icon
            color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            name={ICONS.EDIT}
            size={12}
          ></Icon>
          <Text
            color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            className="L1"
          >
            Edit
          </Text>
        </Button>
      );
    }
    return (
      <Button
        onClick={toggleModal}
        backgroundColor="var(--gray-700)"
        hoverBackgroundColor="var(--gray-1000)"
        className={`${styles['add-button']} flex ai-center gap-6`}
        height="32px"
      >
        <ProjectColorIcon
          name={uniqueDescriptors?.length ? ICONS.EDIT : ICONS.ADD}
          size={12}
          colorVariable={EColorVariablesV2.primary}
        />
        <Text color={getThemeColorCSSVariable()} className="L2">
          Add
        </Text>
      </Button>
    );
  };

  return (
    <>
      <div className={styles['widget']}>
        <Container
          background="var(--gray-900)"
          height="56px"
          borderRadius="8px"
          className="flex ai-center jc-space-between padding-16"
        >
          <Text className="H6">{ESettingsAdvancedWidgetLabels.SESSION_METADATA}</Text>
          <div className={`${styles['descriptor-list']} flex gap-10`}>
            {uniqueDescriptors?.map((desc) => (
              <Container padding="8px 12px" borderRadius="100px" background="var(--gray-700)" key={desc}>
                <Text className="C1" design="GlobalIcoText1" clamp={1} maxWidth="475px">
                  {desc}
                </Text>
              </Container>
            ))}
          </div>
          {renderButton()}
        </Container>
        <SessionMetadataModal
          open={open}
          handleClose={toggleModal}
          metadata={uniqueDescriptors.join(' ')}
          sessions={sessionMap}
        />
      </div>
    </>
  );
};

export default SessionMetadataWidget;
